import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://ms-supermart-production.up.railway.app/api';

// Create axios instance
const api = axios.create({ baseURL: API_URL });

// Add token to every request automatically
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('adminToken');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// AUTH
export const loginAdmin          = (data) => api.post('/auth/admin/login', data);
export const resetCustomerPassword = (phone, password) => api.put('/auth/admin/reset-customer-password', { phone, password });
export const getAdminSessions    = ()     => api.get('/auth/admin/sessions');
export const revokeSession       = (sid)  => api.delete(`/auth/admin/sessions/${sid}`);
export const revokeAllSessions   = ()     => api.delete('/auth/admin/sessions/all');

// CATEGORIES
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);
export const uploadCategoryImage = (id, formData) => api.post(`/categories/${id}/image`, formData);

// PRODUCTS
export const getProducts = (params) => api.get('/products', { params });
export const getProductById = (id) => api.get(`/products/${id}`);
export const recordImportLog = (data) => api.post('/products/imports/log', data);
export const wipeAllProducts = (data) => api.post('/products/wipe-all', data);
export const getDeletedProducts = (search) => api.get('/products/deleted', { params: { search: search || undefined } });
export const restoreProduct     = (id) => api.put(`/products/${id}/restore`);
export const getImportLogs = () => api.get('/products/imports/logs');
export const createProduct = (data) => api.post('/products', data);
export const bulkImportProducts = (rows) => api.post('/products/bulk-import', { rows });
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const getLowStockProducts = () => api.get('/products/low-stock');
export const getExpiringProducts = () => api.get('/products/expiring-soon');
export const getProductByBarcode = (barcode) => api.get(`/billing/barcode/${barcode}`);

// ORDERS
export const getOrders        = (params) => api.get('/orders', { params });
export const getOrderById     = (id)     => api.get(`/orders/${id}`);
export const getOrderDetail   = (id)     => api.get(`/orders/admin-detail/${id}`);
export const updateOrderStatus= (id, data) => api.put(`/orders/${id}/status`, data);
export const assignDriver     = (id, data) => api.put(`/orders/assign-driver/${id}`, data);
export const getLiveTracking  = ()       => api.get('/orders/live-tracking');

// BILLING
export const createBill = (data) => api.post('/billing', data);
export const getTodaysBills = () => api.get('/billing/today');
export const updateBillPayment = (id, payment_method) => api.put(`/billing/${id}/payment`, { payment_method });
// Cashier-safe customer lookup + wallet (name/phone + balance only, no history)
export const lookupBillingCustomer = (phone) => api.get('/billing/customer', { params: { phone } });
export const suggestBillingCustomers = (q) => api.get('/billing/customer-suggest', { params: { q } });
export const billingWalletDebit     = (data)  => api.post('/billing/wallet-debit', data);

// REPORTS
export const getReportsSummary        = (params) => api.get('/reports/summary', { params });
export const getReportsSalesByDay     = (params) => api.get('/reports/sales-by-day', { params });
export const getReportsTopProducts    = (params) => api.get('/reports/top-products', { params });
export const getReportsPaymentMethods = (params) => api.get('/reports/payment-methods', { params });
export const getReportsHourly         = (params) => api.get('/reports/hourly', { params });
export const getDashboardStats        = ()        => api.get('/reports/dashboard');
export const getGSTReport             = (params)  => api.get('/reports/gst', { params });
export const getDayEndReport          = (params)  => api.get('/reports/day-end', { params });
export const getExpiryReport          = ()        => api.get('/reports/expiry');
export const getPnLReport             = (params)  => api.get('/reports/pnl', { params });
export const getReorderSuggestions    = ()        => api.get('/reports/reorder');

// COUPONS
export const getCoupons    = ()         => api.get('/coupons');
export const createCoupon  = (data)     => api.post('/coupons', data);
export const updateCoupon  = (id, data) => api.put(`/coupons/${id}`, data);
export const deleteCoupon  = (id)       => api.delete(`/coupons/${id}`);

// INVENTORY WRITE-OFF
export const writeOffStock   = (data)   => api.post('/inventory/write-off', data);
export const getInventoryLog = (params) => api.get('/inventory/log', { params });

// PURCHASES / GRN
export const getPurchases = (params) => api.get('/purchases', { params });
export const getPurchaseById = (id) => api.get(`/purchases/${id}`);
export const createPurchase = (data) => api.post('/purchases', data);
export const verifyPurchase = (id, data) => api.put(`/purchases/${id}/verify`, data);
export const deletePurchase = (id) => api.delete(`/purchases/${id}`);
export const reversePurchase = (id) => api.post(`/purchases/${id}/reverse`);
export const getLastPurchaseItem = (productId) => api.get(`/purchases/last-item/${productId}`);

// PRODUCT IMAGE UPLOAD
// NOTE: Do NOT set Content-Type manually for FormData — browser must set it with the boundary
export const uploadProductImage  = (id, formData) =>
  api.post(`/products/${id}/image`, formData);
export const adjustProductStock  = (id, data) => api.post(`/products/${id}/adjust-stock`, data);

// PRODUCT EXTRA IMAGES (gallery)
export const getProductImages    = (id)          => api.get(`/products/${id}/images`);
export const uploadProductImages = (id, formData) => api.post(`/products/${id}/images`, formData);
export const deleteProductImage  = (id, imgId)   => api.delete(`/products/${id}/images/${imgId}`);

// STAFF
export const getStaff          = ()           => api.get('/staff');
export const createStaff       = (data)       => api.post('/staff', data);
export const updateStaff       = (id, data)   => api.put(`/staff/${id}`, data);
export const resetStaffPassword= (id, data)   => api.put(`/staff/${id}/reset-password`, data);
export const deleteStaff       = (id)         => api.delete(`/staff/${id}`);

// EXPENSES
export const getExpenses        = (params)     => api.get('/expenses', { params });
export const getExpenseSummary  = (params)     => api.get('/expenses/summary', { params });
export const createExpense      = (data)       => api.post('/expenses', data);
export const updateExpense      = (id, data)   => api.put(`/expenses/${id}`, data);
export const deleteExpense      = (id)         => api.delete(`/expenses/${id}`);

// PROFIT & DEAD STOCK
export const getProfitReport    = (params)     => api.get('/reports/profit', { params });
export const getDeadStockReport = (params)     => api.get('/reports/dead-stock', { params });

// CUSTOMERS
export const getCustomers    = (params) => api.get('/customers', { params });
export const getCustomerById = (id)     => api.get(`/customers/${id}`);
export const deleteCustomer  = (id)     => api.delete(`/customers/${id}`);
export const getCustomerBill = (orderId)=> api.get(`/customers/bill/${orderId}`);

// SUPPLIERS
export const getSuppliers         = (params)     => api.get('/suppliers', { params });
export const getSupplierById      = (id)         => api.get(`/suppliers/${id}`);
export const createSupplier       = (data)       => api.post('/suppliers', data);
export const updateSupplier       = (id, data)   => api.put(`/suppliers/${id}`, data);
export const deleteSupplier       = (id)         => api.delete(`/suppliers/${id}`);
export const recordSupplierPayment= (id, data)   => api.post(`/suppliers/${id}/payment`, data);
export const getSupplierPayments  = (id)         => api.get(`/suppliers/${id}/payments`);

// BATCH TRACKING
export const getProductBatches  = (productId)       => api.get(`/batches/product/${productId}`);
export const addProductBatch    = (productId, data) => api.post(`/batches/product/${productId}`, data);
export const updateBatchSellingPrice = (batchId, selling_price, mrp) => api.put(`/batches/${batchId}/selling-price`, { selling_price, mrp });
export const clearBatch = (batchId, reason) => api.post(`/batches/${batchId}/clear`, { reason });
export const getExpiringBatches = ()                => api.get('/batches/expiring');

// STORE SETTINGS
export const getStoreSettings    = ()       => api.get('/settings');
export const updateStoreSettings = (data)   => api.put('/settings', data);

// EXPIRED STOCK SETTLEMENTS
export const getExpiredSettlements   = (params)      => api.get('/expired-settlements', { params });
export const createExpiredSettlement = (data)         => api.post('/expired-settlements', data);
export const settleExpiredEntry      = (id, data)     => api.put(`/expired-settlements/${id}/settle`, data);
export const getPendingCredits       = (supplier_name)=> api.get('/expired-settlements/pending-credits', { params: { supplier_name } });

// RETURNS & REFUNDS
export const getReturns           = (params) => api.get('/returns', { params });
export const getReturnById        = (id)     => api.get(`/returns/${id}`);
export const createReturn         = (data)   => api.post('/returns', data);
export const approveReturn        = (id)     => api.put(`/returns/${id}/approve`);
export const rejectReturn         = (id)     => api.put(`/returns/${id}/reject`);
export const searchOrderForReturn = (q)      => api.get('/returns/search-order', { params: { q } });

// CUSTOMER LOYALTY / WALLET
export const getAllWallets          = (params)        => api.get('/loyalty', { params });
export const getCustomerWallet      = (customerId)    => api.get(`/loyalty/${customerId}`);
export const getWalletTransactions  = (customerId)    => api.get(`/loyalty/${customerId}/transactions`);
export const creditCustomerWallet   = (customerId, d) => api.post(`/loyalty/${customerId}/credit`, d);
export const debitCustomerWallet    = (customerId, d) => api.post(`/loyalty/${customerId}/debit`, d);

// SUPPLIER RETURNS
export const getSupplierReturns    = (params) => api.get('/supplier-returns', { params });
export const createSupplierReturn  = (data)   => api.post('/supplier-returns', data);
export const updateSupplierReturn  = (id, data) => api.put(`/supplier-returns/${id}/status`, data);

// MONTHLY SALES TARGET
export const getMonthlyTarget   = (month)  => api.get('/targets/monthly', { params: { month } });
export const setMonthlyTarget   = (data)   => api.post('/targets/monthly', data);
export const getTargetHistory   = ()       => api.get('/targets/history');

// STAFF ATTENDANCE
export const clockIn             = (data)          => api.post('/attendance/clock-in', data);
export const clockOut            = (data)          => api.post('/attendance/clock-out', data);
export const markAttendance      = (data)          => api.post('/attendance/mark', data);
export const getTodayAttendance  = ()              => api.get('/attendance/today');
export const getAttendance       = (params)        => api.get('/attendance', { params });
export const getAttendanceSummary= (params)        => api.get('/attendance/summary', { params });

// CASH DRAWER
export const openCashDrawer      = (data)  => api.post('/cash-drawer/open', data);
export const closeCashDrawer     = (id, d) => api.post(`/cash-drawer/${id}/close`, d);
export const getTodayCashDrawer  = ()      => api.get('/cash-drawer/today');
export const getCashDrawerSessions=(params)=> api.get('/cash-drawer', { params });

// CUSTOMER PURCHASE HISTORY
export const getCustomerHistory  = (id, params) => api.get(`/customers/${id}/history`, { params });

// BULK PRICE UPDATE
export const bulkPriceUpdate     = (updates) => api.post('/products/bulk-price-update', { updates });

// PAYROLL
export const getPayroll          = (month)   => api.get('/payroll', { params: { month } });
export const setStaffSalary      = (id, data)=> api.put(`/payroll/${id}/salary`, data);
export const markPayrollPaid     = (id, data)=> api.post(`/payroll/${id}/mark-paid`, data);
export const markPayrollPending  = (id, data)=> api.post(`/payroll/${id}/mark-pending`, data);
export const getPayrollHistory   = (id)      => api.get(`/payroll/${id}/history`);

// SUPPLIER LEDGER
export const getSupplierLedger   = (id)      => api.get(`/suppliers/${id}/ledger`);

// CATEGORY SALES
export const getCategorySales    = (params)  => api.get('/reports/category-sales', { params });

// PRODUCT VARIANTS
export const getProductVariants  = (id)          => api.get(`/products/${id}/variants`);
export const addProductVariant   = (id, data)     => api.post(`/products/${id}/variants`, data);
export const updateProductVariant= (id, vid, data)=> api.put(`/products/${id}/variants/${vid}`, data);
export const deleteProductVariant= (id, vid)      => api.delete(`/products/${id}/variants/${vid}`);

export default api;